import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import {
  exportWorkerTimesheetsExcel,
  fetchWorkerTimesheetReportMonths,
} from '../../clients/worker_timesheet_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'WorkerTimesheetMonthlyExportModal';

const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

/**
 * @param {string} yyyyMm - `YYYY-MM`
 * @returns {string}
 */
function formatMonthLabel(yyyyMm) {
  const [year, monthPart] = String(yyyyMm).split('-');
  const monthIndex = Number(monthPart) - 1;
  if (!year || monthIndex < 0 || monthIndex > 11) return yyyyMm;
  return `${HEBREW_MONTHS[monthIndex]} ${year}`;
}

/**
 * @param {{
 *   show: boolean,
 *   onHide: () => void,
 *   token: string|null|undefined,
 *   onExportComplete?: () => void,
 * }} props
 */
const WorkerTimesheetMonthlyExportModal = ({
  show,
  onHide,
  token,
  onExportComplete,
}) => {
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [isLoadingMonths, setIsLoadingMonths] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const fetchAbortRef = useRef(null);
  const exportAbortRef = useRef(null);
  const isMountedRef = useRef(true);

  const resetModalState = useCallback(() => {
    setMonths([]);
    setSelectedMonth('');
    setLoadError('');
    setIsLoadingMonths(false);
    setIsExporting(false);
  }, []);

  const handleClose = useCallback(() => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
      fetchAbortRef.current = null;
    }
    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
      exportAbortRef.current = null;
    }
    resetModalState();
    onHide();
  }, [onHide, resetModalState]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
      if (exportAbortRef.current) exportAbortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (!show || !token) return;

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setIsLoadingMonths(true);
    setLoadError('');
    setMonths([]);
    setSelectedMonth('');

    logInfo(LOG_CONTEXT, 'load months start');

    fetchWorkerTimesheetReportMonths(token, { signal: controller.signal }).then(
      result => {
        if (!isMountedRef.current || fetchAbortRef.current !== controller) {
          return;
        }
        fetchAbortRef.current = null;
        setIsLoadingMonths(false);

        if (result?.canceled) return;
        if (!result?.success) {
          logError(LOG_CONTEXT, 'load months failed', {
            detail: result?.error,
          });
          setLoadError('לא ניתן לטעון חודשים עם דיווחים. נסו שוב.');
          return;
        }

        const list = result.months || [];
        setMonths(list);
        if (list.length > 0) {
          setSelectedMonth(list[0]);
        }
        logInfo(LOG_CONTEXT, 'load months success', { monthCount: list.length });
      },
    );
  }, [show, token]);

  const handleExport = async () => {
    if (!token || !selectedMonth || isExporting) return;

    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
    }
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setIsExporting(true);

    logInfo(LOG_CONTEXT, 'export start', { month: selectedMonth });

    const result = await exportWorkerTimesheetsExcel(token, {
      monthFilter: selectedMonth,
      signal: controller.signal,
    });

    if (!isMountedRef.current || exportAbortRef.current !== controller) {
      return;
    }
    exportAbortRef.current = null;
    setIsExporting(false);

    if (result?.canceled) return;
    if (!result?.success) {
      logError(LOG_CONTEXT, 'export failed', {
        month: selectedMonth,
        status: result?.status,
        detail: result?.detail,
      });
      showToast('לא ניתן לייצא דוח חודשי. נסו שוב.', 'error', 'top-center', 3000);
      return;
    }

    logInfo(LOG_CONTEXT, 'export success', {
      month: selectedMonth,
      filename: result.filename,
    });
    onExportComplete?.();
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" centered dir="rtl" lang="he">
      <Modal.Header closeButton>
        <Modal.Title>ייצוא דוח חודשי (אקסל)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingMonths ? (
          <div className="text-center py-3">
            <Spinner animation="border" role="status" />
            <div className="small text-muted mt-2">טוען חודשים עם דיווחים…</div>
          </div>
        ) : loadError ? (
          <p className="text-danger mb-0">{loadError}</p>
        ) : months.length === 0 ? (
          <p className="text-muted mb-0">אין דיווחים לייצוא</p>
        ) : (
          <Form.Group>
            <Form.Label className="small text-muted">בחרו חודש</Form.Label>
            <Form.Select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              aria-label="חודש לייצוא דוח"
            >
              {months.map(month => (
                <option key={month} value={month}>
                  {formatMonthLabel(month)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={isExporting}>
          ביטול
        </Button>
        <Button
          variant="success"
          onClick={handleExport}
          disabled={
            isLoadingMonths ||
            Boolean(loadError) ||
            months.length === 0 ||
            !selectedMonth ||
            isExporting
          }
        >
          {isExporting ? (
            <>
              <Spinner animation="border" size="sm" className="ms-2" />
              מייצא…
            </>
          ) : (
            'ייצוא'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkerTimesheetMonthlyExportModal;
